import { createContext, useContext } from "react";
import {
  canAt,
  CONSOLE_ALSO_OPENS,
  inPlatformConsole,
  isFeatureEnabledAt,
  NAVIGATION,
  type NavItem,
  type PolicySubject,
} from "@alm/policy";
import type { Me } from "@alm/api-contracts";
import { canHere, featureHere } from "@/lib/policy/policy-here";
import type { Permission, FeatureKey } from "@alm/policy";

export interface PolicyValue {
  me: Me;
  subject: PolicySubject;
  /** At the building being looked at unless one is named; `null` names none (`canHere`). */
  can: (permission: Permission, facilityId?: string | null) => boolean;
  feature: (key: FeatureKey, facilityId?: string | null) => boolean;
  /** Sidebar entries this user may open at the building they are looking at. */
  navigation: NavItem[];
}

/** The entries a person may open at one building, or at any of theirs (null). */
export function navigationFor(subject: PolicySubject, facilityId: string | null): NavItem[] {
  return NAVIGATION.filter((entry) => {
    const inConsole = inPlatformConsole(subject);
    if (entry.platformOnly) return inConsole;
    if (!subject.organization) return inConsole && CONSOLE_ALSO_OPENS.has(entry.id);
    return (
      canAt(subject, entry.permission, facilityId) &&
      isFeatureEnabledAt(subject, entry.id, facilityId)
    );
  });
}

/** The policy as it reads at one building, or across all of the person's (null). */
export function policyAt(me: Me, subject: PolicySubject, here: string | null): PolicyValue {
  return {
    me,
    subject,
    can: (permission, facilityId) => canHere(subject, permission, here, facilityId),
    feature: (key, facilityId) => featureHere(subject, key, here, facilityId),
    navigation: navigationFor(subject, here),
  };
}

export const PolicyContext = createContext<PolicyValue | null>(null);

export function usePolicy(): PolicyValue {
  const value = useContext(PolicyContext);
  if (!value) throw new Error("usePolicy must be used inside PolicyProvider");
  return value;
}
